import { useEffect, useMemo, useRef, useState } from 'react';
import { formatToUSFullDate } from 'utils/dateTimeUtils/dateFormatUtil';
import { isDateAfter, isDateValid, parseDateString } from 'utils/dateTimeUtils/dateValidationUtil';

import { useEvent } from '@american-autonomy/ag-ui';

import { COMPLETE_DATE_LENGTH, DATE_FORMAT } from './constants';
import type { Props, UseSingleDatePickerReturn } from './types';

const useSingleDatePicker = ({ maxDate, onChange, value }: Props): UseSingleDatePickerReturn => {
    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const popoverRef = useRef<HTMLDivElement | null>(null);
    const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);
    const [inputValue, setInputValue] = useState<string | undefined>(
        value ? formatToUSFullDate(value) : undefined
    );

    // Selection is capped at today unless the consumer narrows it further.
    const effectiveMaxDate = useMemo(() => maxDate ?? new Date(), [maxDate]);

    // Writes a date (or its absence) into all three mirrors of the current value:
    // selection state, controlled input state, and the uncontrolled input element.
    const syncDate = (date?: Date) => {
        const formatted = date ? formatToUSFullDate(date) : '';

        setSelectedDate(date);
        setInputValue(formatted || undefined);

        if (inputRef.current) {
            inputRef.current.value = formatted;
        }
    };

    // Sync when the consumer's value changes after mount. The edit modal unmounts
    // when closed, so this is not a reopen path — the real case is a consumer whose
    // seed lands one render after mount (useEditDraft reseeds its draft in an effect,
    // so the record's date arrives on the render after this hook first ran). The
    // timestamp guard keeps identity-only churn and self-echoes from onChange from
    // resetting typed input.
    useEffect(() => {
        if (value && !isDateValid(value)) {
            return;
        }

        if (value?.getTime() === selectedDate?.getTime()) {
            return;
        }

        syncDate(value);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedDate is the sync target, not a trigger
    }, [value]);

    const showPopover = () => {
        const element = popoverRef.current as HTMLElement & { showPopover?: VoidFunction };

        element?.showPopover?.();
    };

    const hidePopover = () => {
        const element = popoverRef.current as HTMLElement & { hidePopover?: VoidFunction };

        element?.hidePopover?.();
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as HTMLElement;
        const isInsideDropdown = popoverRef.current?.contains(target);
        const isInsideInput = containerRef.current?.contains(target);

        if (!isInsideDropdown && !isInsideInput) {
            hidePopover();
        }
    };

    useEvent({ event: 'mousedown', handler: handleClickOutside });

    const commitDate = (date: Date) => {
        syncDate(date);
        onChange?.(date);
        hidePopover();
    };

    const clearDate = () => {
        setSelectedDate(undefined);
        setInputValue(undefined);
        onChange?.(undefined);
    };

    const handleSelect = (date?: Date) => {
        if (!date) {
            clearDate();

            return;
        }

        const pickedDate = new Date(date);

        pickedDate.setHours(0);
        commitDate(pickedDate);
    };

    const handleTextChange = () => {
        const rawValue = inputRef.current?.value ?? '';

        setInputValue(rawValue || undefined);

        if (!rawValue) {
            clearDate();

            return;
        }

        if (rawValue.length !== COMPLETE_DATE_LENGTH) {
            return;
        }

        const parsedDate = parseDateString(rawValue, DATE_FORMAT);

        if (!isDateValid(parsedDate)) {
            return;
        }

        parsedDate.setHours(0);

        if (isDateAfter(parsedDate, effectiveMaxDate)) {
            return;
        }

        commitDate(parsedDate);
    };

    const handleBlur = () => {
        const typedLength = inputRef.current?.value.length ?? 0;
        const isStable = typedLength === 0 || typedLength === COMPLETE_DATE_LENGTH;

        if (!isStable) {
            return;
        }

        if (selectedDate && isDateValid(selectedDate)) {
            // Restore to the last valid selected date.
            setInputValue(formatToUSFullDate(selectedDate));

            return;
        }

        // No valid selected date — clear only if the typed value is itself an
        // invalid (unparseable) date, mirroring DateRangePicker's blur behavior.
        const currentValue = inputRef.current?.value ?? '';
        const parsedDate = parseDateString(currentValue, DATE_FORMAT);

        if (!isDateValid(parsedDate)) {
            setInputValue(undefined);
        }
    };

    const handleOpen = () => {
        inputRef.current?.focus();
        showPopover();
    };

    return {
        containerRef,
        effectiveMaxDate,
        handleBlur,
        handleOpen,
        handleSelect,
        handleTextChange,
        inputRef,
        inputValue,
        popoverRef,
        selectedDate
    };
};

export default useSingleDatePicker;
